import { AGENT_CARD_RESULT_KIND, drawnCardKind, type AgentCardResult } from '@numeo/types'
import type { TmsInvoiceDetail, TmsInvoiceRow, TmsInvoiceWorklist, TmsLoadDocumentView, TmsLoadRow, TmsWriteRow } from '@numeo/types-main-web-app'
import type { GeneralChatMessage } from '../../types'

/**
 * How much of a list a card's answer holds, as the list cards report it beside their rows: a page of a tab (`total`, `shown`,
 * `next_page` when more remain, and the page's first row's position) or a named read cut at its cap (`truncated`, no total).
 */
export interface ListPage {
    shown: number
    total?: number
    nextPage?: number
    /** The position of the page's first row, counted from 1; 1 on the first page and on a named read. */
    first: number
    truncated: boolean
}

/** How a card's answer is drawn (plan FR-G3): a table of rows, a preview of one thing, a write's outcome per load, or a load's document. */
export type CardView =
    | { view: 'invoices'; rows: TmsInvoiceRow[]; page?: ListPage }
    | { view: 'invoice-preview'; invoices: TmsInvoiceDetail[]; page?: ListPage }
    | { view: 'loads'; rows: TmsLoadRow[]; page?: ListPage }
    | { view: 'load-preview'; load: TmsLoadRow }
    | { view: 'writes'; card: string; rows: TmsWriteRow[] }
    | { view: 'document'; document: TmsLoadDocumentView }
    | { view: 'worklist'; worklist: TmsInvoiceWorklist }

/** A card's answer in the thread, at the time its result was stored. */
export interface ChatCard {
    id: string
    createdAt: string
    card: string | null
    view: CardView
}

/** A load the thread has shown: its number, and its id when the card named it. */
export interface ThreadLoad {
    number: string
    id?: string
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value)

/** A tool result's `AgentCardResult`, or null for any other tool's output. */
export function parseCardResult(output: string | null | undefined): AgentCardResult | null {
    if (!output || !output.startsWith('{')) return null
    try {
        const parsed: unknown = JSON.parse(output)
        if (!isRecord(parsed) || parsed.kind !== AGENT_CARD_RESULT_KIND || !isRecord(parsed.structured)) return null
        return {
            kind: AGENT_CARD_RESULT_KIND,
            card: typeof parsed.card === 'string' ? parsed.card : null,
            text: String(parsed.text ?? ''),
            structured: parsed.structured,
        }
    } catch {
        return null
    }
}

const rowsOf = (structured: Record<string, unknown>): Record<string, unknown>[] | null =>
    Array.isArray(structured.rows) && structured.rows.every(isRecord) ? (structured.rows as Record<string, unknown>[]) : null

const isCount = (value: unknown): value is number => typeof value === 'number' && Number.isInteger(value) && value >= 0

/** The list's totals a card answered beside its rows; undefined when it carries none (an older answer). */
export function pageOf(structured: Record<string, unknown>): ListPage | undefined {
    if (!isCount(structured.shown)) return undefined
    const page = isCount(structured.page) && structured.page > 0 ? structured.page : 1
    const limit = isCount(structured.limit) ? structured.limit : 0
    return {
        shown: structured.shown,
        ...(isCount(structured.total) ? { total: structured.total } : {}),
        ...(isCount(structured.next_page) ? { nextPage: structured.next_page } : {}),
        first: (page - 1) * limit + 1,
        truncated: structured.truncated === true,
    }
}

/** One row is a preview only when it is the whole list: a page or a cut read of more stays a table, with its totals. */
const isWholeList = (page: ListPage | undefined): boolean => !page || (!page.truncated && (page.total === undefined || page.total <= 1))

/**
 * Which view a card's answer gets; null when the chat leaves it to the model's own words. WHETHER it is drawn is `drawnCardKind`
 * (`@numeo/types`), the rule general-agent-service also reads to keep the model from repeating it; this picks the view's shape.
 */
export function viewOfCard(card: string | null, structured: Record<string, unknown>): CardView | null {
    const kind = drawnCardKind(card, structured)
    if (kind === 'document') return { view: 'document', document: structured as unknown as TmsLoadDocumentView }
    if (kind === 'worklist') return { view: 'worklist', worklist: structured as unknown as TmsInvoiceWorklist }
    const rows = rowsOf(structured) ?? []
    // A write card's answer names the card itself and carries one outcome per load (TmsWriteResult).
    if (kind === 'writes')
        return { view: 'writes', card: typeof structured.card === 'string' ? structured.card : (card ?? ''), rows: rows as unknown as TmsWriteRow[] }
    const page = pageOf(structured)
    const paged = page ? { page } : {}
    if (kind === 'invoices') {
        if (rows.every((row) => Array.isArray(row.lines))) return { view: 'invoice-preview', invoices: rows as unknown as TmsInvoiceDetail[], ...paged }
        return { view: 'invoices', rows: rows as unknown as TmsInvoiceRow[], ...paged }
    }
    if (kind === 'loads') {
        const loads = rows as unknown as TmsLoadRow[]
        return loads.length === 1 && isWholeList(page) ? { view: 'load-preview', load: loads[0] } : { view: 'loads', rows: loads, ...paged }
    }
    return null
}

/** The cards a tab's tool results draw, oldest first. */
export function cardsOfRows(rows: GeneralChatMessage[]): ChatCard[] {
    return rows.flatMap((row) => {
        const result = parseCardResult(row.tool_output)
        const view = result && viewOfCard(result.card, result.structured)
        return view ? [{ id: row.id, createdAt: row.created_at, card: result.card, view }] : []
    })
}

/** Every load the cards showed, newest first, once each; an id is kept from whichever card named it. */
export function threadLoadsOf(cards: ChatCard[]): ThreadLoad[] {
    const byNumber = new Map<string, ThreadLoad>()
    const add = (number: string | null | undefined, id?: string) => {
        if (!number) return
        const known = byNumber.get(number)
        byNumber.set(number, { number, id: known?.id ?? id })
    }
    for (const { view } of [...cards].reverse()) {
        // A Ready-tab row is one load and names its id; an invoice row may hold several and names none.
        if (view.view === 'invoices')
            view.rows.forEach((row) => row.load_numbers.forEach((n) => add(n, row.load_numbers.length === 1 ? row.load_id : undefined)))
        else if (view.view === 'invoice-preview') view.invoices.forEach((inv) => inv.load_numbers.forEach((n) => add(n)))
        else if (view.view === 'loads') view.rows.forEach((row) => add(row.load_number, row.load_id))
        else if (view.view === 'load-preview') add(view.load.load_number, view.load.load_id)
        else if (view.view === 'document') add(view.document.load_number, view.document.load_id)
        else if (view.view === 'worklist') {
            const { ready, needs_documents: needs, delivery_passed: passed } = view.worklist
            ;[...ready.rows, ...needs.rows].forEach((row) => add(row.load_numbers[0], row.load_id))
            passed?.loads.forEach((load) => add(load.load_number, load.load_id))
        } else view.rows.forEach((row) => add(row.load_number, row.load_id))
    }
    return [...byNumber.values()]
}

/** A thread item: a message bubble or a card, in the order they happened. */
type ThreadItem = { kind: 'message'; message: GeneralChatMessage } | { kind: 'card'; card: ChatCard }

/** Messages and cards by time; a card stored at the same moment as a message goes before it (the reply follows its tools). */
export function threadOf(messages: GeneralChatMessage[], cards: ChatCard[]): ThreadItem[] {
    const items: Array<{ item: ThreadItem; at: number; order: number }> = [
        ...cards.map((card, order) => ({ item: { kind: 'card' as const, card }, at: Date.parse(card.createdAt), order })),
        ...messages.map((message, i) => ({ item: { kind: 'message' as const, message }, at: Date.parse(message.created_at), order: cards.length + i })),
    ]
    return items.sort((a, b) => a.at - b.at || a.order - b.order).map(({ item }) => item)
}
